import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "./L";
import { Menu, X, ChevronDown, Phone, Globe } from "lucide-react";
import { cities, contactInfo, cityLabel, LOGO } from "../mock";
import { useLang } from "../context/LanguageContext";

const Logo = () => (
  <Link to="/" className="flex items-center">
    <img src={LOGO} alt="MyCleaner.ch – Putzfrau-Service Schweiz" className="h-9 md:h-10 w-auto" />
  </Link>
);

const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { label: t.nav.howItWorks, path: "/so-funktionierts" },
    { label: t.nav.pricing, path: "/preise" },
    { label: t.nav.contact, path: "/kontakt" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  const LangBtn = ({ mobile }) => (
    <button onClick={toggle} className={`inline-flex items-center gap-1.5 rounded-lg font-semibold transition-colors ${mobile ? "px-3 py-2 text-sm bg-slate-100 text-slate-700" : "px-3 py-2 text-[13px] text-slate-600 hover:text-teal-700 hover:bg-teal-50"}`}>
      <Globe className="w-4 h-4" /> {t.switchTo}
    </button>
  );

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"}`}>
      <div className="hidden md:block bg-teal-700 text-white">
        <div className="max-w-[1200px] mx-auto px-6 h-9 flex items-center justify-between text-[12.5px] font-medium">
          <span>{t.topStrip}</span>
          <a href={contactInfo.phoneHref} className="flex items-center gap-1.5 hover:text-teal-100"><Phone className="w-3.5 h-3.5" /> {contactInfo.phone}</a>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 h-[68px] flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          <div className="relative group">
            <Link to="/staedte" className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-[15px] font-medium text-slate-700 hover:text-teal-700 hover:bg-teal-50 transition-colors">
              {t.nav.cities} <ChevronDown className="w-4 h-4" />
            </Link>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="w-[460px] bg-white rounded-xl shadow-xl border border-slate-100 p-3 grid grid-cols-2 gap-1">
                {cities.map((c) => (
                  <Link key={c.slug} to={`/putzfrau/${c.slug}`} className="px-3 py-2 rounded-lg text-[14px] text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors">
                    {t.term} {cityLabel(c, lang)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          {links.map((l) => (
            <Link key={l.path} to={l.path} className={`px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === l.path ? "text-teal-700 bg-teal-50" : "text-slate-700 hover:text-teal-700 hover:bg-teal-50"}`}>
              {l.label}
            </Link>
          ))}
          <Link data-testid="nav-become-cleaner" to="/putzfrau-werden" className={`px-3.5 py-2 rounded-lg text-[15px] font-semibold transition-colors ${location.pathname === "/putzfrau-werden" ? "text-teal-700 bg-teal-50" : "text-teal-700 hover:bg-teal-50"}`}>
            {t.nav.becomeCleaner}
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LangBtn />
          <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-semibold text-[15px] px-6 py-3 rounded-xl hover:bg-teal-800 transition-colors shadow-sm">
            {t.nav.book}
          </Link>
        </div>

        <button className="lg:hidden text-slate-800" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className={`lg:hidden overflow-y-auto transition-all duration-300 bg-white border-t border-slate-100 ${open ? "max-h-[80vh]" : "max-h-0"}`}>
        <div className="px-5 py-4 flex flex-col gap-1">
          <div className="flex items-center justify-between mb-1">
            <Link to="/staedte" className="py-2.5 text-[15px] font-semibold text-slate-800">{t.nav.allCities}</Link>
            <LangBtn mobile />
          </div>
          <div className="grid grid-cols-2 gap-1 pb-2 border-b border-slate-100 mb-2">
            {cities.slice(0, 8).map((c) => (
              <Link key={c.slug} to={`/putzfrau/${c.slug}`} className="py-2 text-[14px] text-slate-600">{t.term} {cityLabel(c, lang)}</Link>
            ))}
          </div>
          {links.map((l) => (
            <Link key={l.path} to={l.path} className="py-2.5 text-[15px] font-medium text-slate-700">{l.label}</Link>
          ))}
          <Link data-testid="nav-become-cleaner-mobile" to="/putzfrau-werden" className="py-2.5 text-[15px] font-semibold text-teal-700">{t.nav.becomeCleaner}</Link>
          <Link to="/buchen" className="mt-3 text-center bg-teal-700 text-white font-semibold px-6 py-3 rounded-xl">{t.nav.book}</Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
